import React, {useEffect, useRef, useState} from 'react';
import {useCookies} from 'react-cookie';
import './Map.css';
import {GoogleMap, Marker, useJsApiLoader} from '@react-google-maps/api';
import BuildingInfo from '../../components/BuildingInfo/BuildingInfo';
import NewMapFilter from '../../components/MapFilter/NewMapFilter';
import FilterImg from '../../assets/filter.png';
import {darkMapStyles} from "../../../utils/constants/styles";
import {BuildingsRepository} from "../../../repositories/buildings";
import {toast} from "sonner";
import {ReactiveBuilder} from "../../components/Loader/loader";
import RadialMenu from "../../components/RadialMenu/RadialMenu";
import { getStatusDetails, statusLegend } from "../../../utils/statusConfig";

const getWorstStatusForBuilding = (building) => {
    if (!building?.bankInfos) {
        return null;
    }

    let worstStatus = 0;
    let hasOfflineDevice = false;

    building.bankInfos.forEach(bank => {
        bank.deviceInfos?.forEach(device => {
            if (device.status === 10) {
                hasOfflineDevice = true;
            }
            if (device.status > worstStatus) {
                worstStatus = device.status;
            }
        });
    });

    // If the worst status is 'Running' (20) but there's an 'Offline' device (10),
    // then 'Offline' is considered worse in this context.
    if (worstStatus === 20 && hasOfflineDevice) {
        return 10;
    }

    return worstStatus > 0 ? worstStatus : null;
};

const MapContainer = () => {
    const [cookies] = useCookies();
    const [buildings, setBuildings] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [selectedEntity, setSelectedEntity] = useState(null);
    const [mapContainerWidth, setMapContainerWidth] = useState('100%');
    const [mapContainerHeight, setMapContainerHeight] = useState('100%');
    const [selectedStatuses, setSelectedStatuses] = useState([]);
    const [searchValue, setSearchValue] = useState('');
    const [showFilterMenu, setShowFilterMenu] = useState(false);
    const [isLegendExpanded, setIsLegendExpanded] = useState(true);
    const [mapClicked, setMapClicked] = useState(false);

    const defaultLat = cookies.defaultLat || 0;
    const defaultLng = cookies.defaultLng || 0;

    const [mapCenter, setMapCenter] = useState({
        lat: defaultLat,
        lng: defaultLng,
    });

    const [mapZoom, setMapZoom] = useState(11);
    const [showResetButton, setShowResetButton] = useState(false);
    const mapRef = useRef(null);

    // Load Google Maps API
    const { isLoaded } = useJsApiLoader({
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    });


    const onLoad = (map) => {mapRef.current = map;};
    const handleMapDragEnd = () => {
        if (mapRef.current) {
            const newCenter = {
                lat: mapRef.current.getCenter().lat(),
                lng: mapRef.current.getCenter().lng(),
            };

            const movedTooFar =
                Math.abs(newCenter.lat - defaultLat) > 0.001 ||
                Math.abs(newCenter.lng - defaultLng) > 0.001;

            setShowResetButton(movedTooFar);
        }
    };

    const resetMapCenter = () => {
        setMapCenter({ lat: defaultLat, lng: defaultLng });
        setMapZoom(11);
        setShowResetButton(false);
    };

    async function getBuildings() {
        const response = await BuildingsRepository.getBuildings();
        if (!response.success) {
            toast.error(response.error);
            return;
        }
        setBuildings(response.data);
        setFilteredData(response.data);
    }

    useEffect(() => {
        getBuildings().then(() => {
            toast.success('Buildings loaded!');
        });
    }, []);

    useEffect(() => {
        let newFilteredData;

        if (mapZoom < 15) {
            // Show buildings
            newFilteredData = buildings;
        } else {
            // Show devices from buildings
            newFilteredData = buildings.flatMap(building =>
                building.bankInfos?.flatMap(bank => bank.deviceInfos) || []
            );
        }

        // Apply filters (selected statuses + search value)
        if (selectedStatuses.length > 0) {
            newFilteredData = newFilteredData.filter(item =>
                item.deviceInfos
                    ? item.bankInfos.some(bank =>
                        bank.deviceInfos.some(device => selectedStatuses.includes(device.status))
                    )
                    : selectedStatuses.includes(item.status) // Applies to devices
            );
        }

        if (searchValue) {
            newFilteredData = newFilteredData.filter(item =>
                item.buildingName?.toLowerCase().includes(searchValue.toLowerCase())
            );
        }

        setFilteredData(newFilteredData);
    }, [mapZoom, buildings, selectedStatuses, searchValue]);

// Detect zoom changes
    const handleZoomChanged = () => {
        if (mapRef.current) {
            setMapZoom(mapRef.current.getZoom());
        }
    };


    const itemSelectionHandler = (item) => {
        if (!item) {
            // When the item is null, it means they have deselected whatever it was they selected, so I remove the
            // device from the entity and leave only the building (until they close down the actual info dialog then we
            // bin it all proper like.
            setSelectedEntity({ building: selectedEntity.building });
            return;
        }
        const isBuilding = !item.deviceId;
        let device;
        if (isBuilding && item.bankInfos?.length > 0) {
            device = item.bankInfos.flatMap(bank => bank.deviceInfos)[0] || null;
        } else if (!isBuilding) {
            device = item;
        }
        setMapContainerWidth('50%');
        setSelectedEntity({
                building: isBuilding ? item : buildings.find(
                    (building) => building.buildingId === item.buildingId),
            device: device,
            }
        );
        setMapCenter({lat: (device || item).latitude, lng: (device || item).longitute});
        setMapZoom(device ? 20 : mapZoom);
        setIsLegendExpanded(false)
        setTimeout(() => {
            setMapClicked(true);
        }, 1000);
    };

    const closeBuildingInfoOverlay = ()=> {
        setShowResetButton(false);
        setShowResetButton(false);
        setMapClicked(false);
        setMapContainerWidth('100%');
        setIsLegendExpanded(true);
        setSelectedEntity(null);
        resetMapCenter();
    }

    const handleStatusChange = (status, isChecked) => {
        setSelectedStatuses(prev => (isChecked ? [...prev, status] : prev.filter(s => s !== status)));
    };

    const toggleSidebar = () => setShowFilterMenu(prevState => !prevState);

    const getMarkerIcon = ({item, isBuilding}) => {
        let statusId;
        if (isBuilding) {
            statusId = getWorstStatusForBuilding(item);
        } else {
            statusId = item.status;
        }

        if (isBuilding) {
            const statusDetails = getStatusDetails(statusId);
            return {
                path: window.google.maps.SymbolPath?.CIRCLE,
                fillColor: statusDetails ? statusDetails.color : '#fff',
                fillOpacity: 1,
                strokeWeight: 1,
                strokeColor: '#4B9BFF',
                scale: 12,
            };
        }
        const statusDetails = getStatusDetails(statusId);
        return statusDetails
            ? {
                path: window.google.maps.SymbolPath?.CIRCLE,
                fillColor: statusDetails.color,
                fillOpacity: 1,
                strokeWeight: 1,
                strokeColor: '#FFFFFF',
                scale: 12,
            }
            : null;
    };

    return (
        <div className={`map-container ${mapClicked ? 'map-clicked' : ''}`}>
            <ReactiveBuilder.Page loading={!isLoaded}>
                <GoogleMap
                    mapContainerStyle={{ width: mapContainerWidth, height: mapContainerHeight }}
                    center={mapCenter}
                    zoom={mapZoom}
                    options={{ styles: darkMapStyles, disableDefaultUI: true, zoomControl: true }}
                    onLoad={onLoad}
                    onDragEnd={handleMapDragEnd}
                    onZoomChanged={handleZoomChanged}
                >
                    {filteredData.map(item => {
                        const isBuilding = !item.deviceId;
                        let deviceCount = 0;
                        if (isBuilding && item.bankInfos?.length > 0) {
                            deviceCount = item.bankInfos.reduce((acc, bank) => acc + (bank.deviceInfos?.length || 0), 0);
                        }
                        const statusDetails = getStatusDetails(item.status);
                        return (
                            <Marker
                                key={item.deviceId || item.buildingId}
                                position={{ lat: item.latitude, lng: item.longitute }}
                                icon={getMarkerIcon({ item: item, isBuilding: isBuilding })}
                                title={item.buildingName || statusDetails?.name || 'Unknown'}
                                // label will be shown only for buildings and it will be the device count
                                label={isBuilding ? {text: deviceCount.toString(), color: '#fff'/* , fontSize: '12px' */, fontWeight: 'bold'} : undefined}

                                onClick={() => itemSelectionHandler(item)}
                            />
                        );
                    })}

                </GoogleMap>
            </ReactiveBuilder.Page>
            {(showResetButton && !selectedEntity && !showFilterMenu) && (
                <button className="reset-button" onClick={resetMapCenter}>
                    🔄 Back to Center
                </button>
            )}
            {!!selectedEntity?.device && (<RadialMenu device={selectedEntity.device} />)}

            {selectedEntity && (
                <BuildingInfo
                    data={selectedEntity}
                    onClose={closeBuildingInfoOverlay}
                    handleDeviceClick={itemSelectionHandler}
                />
            )}

            {(!showFilterMenu && !selectedEntity && !mapClicked) && <img src={FilterImg} onClick={toggleSidebar} className="toggle-filter-menu-btn" alt="Filter Toggle" />}

            <div className={`elevator-legend-container ${!isLegendExpanded ? 'collapsed' : ''}`}>
                <div className="legend-header" onClick={() => setIsLegendExpanded(!isLegendExpanded)}>
                    <h4>Status Legend</h4>
                    <span className={`toggle-icon ${!isLegendExpanded && 'ms-2'}`}>{isLegendExpanded ? '−' : '+'}</span>
                </div>
                {isLegendExpanded && (
                    <ul>
                        {statusLegend.map(item => (
                            <li key={item.name}>
                                <div className="legend-item">
                                    <div className="icon-status-wrapper">
                                        <div className="icon-wrapper" style={{ backgroundColor: item.color }}>
                                            <item.icon size={20} color="#FFFFFF" />
                                        </div>
                                        <span className="status-label">{item.name}</span>
                                    </div>
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            {showFilterMenu && <NewMapFilter onStatusChange={handleStatusChange} setSearchValue={setSearchValue} onClose={toggleSidebar} selectedStatuses={selectedStatuses} />}
        </div>
    );
};

export default MapContainer;
